import { Node } from 'reactflow';
import { WorkflowBaseService } from './WorkflowBaseService';
import { AgentBaseService } from '@/features/Agents/services/agentBaseService';
import { SessionService } from '@/features/Auth/services/SessionService';
import { NodeDefinitionService } from './NodeDefinitionService';
import { getApi, postApi, engineApiBase } from '@/shared/utils/fetchApi';

/**
 * Formata a configuração do node, processando placeholders
 */
export class NodeService {
  /**
   * Formata a configuração do node, processando placeholders
   */
  static formatNodeConfig(config: Record<string, unknown>, inputData: Record<string, unknown> = {}): Record<string, unknown> {
    // Clone a configuração para não modificar o original
    const formattedConfig = JSON.parse(JSON.stringify(config));
    
    // Função recursiva para processar valores
    const processValue = (value: unknown): unknown => {
      if (typeof value === 'string') {
        // Processa placeholders ${input.field}
        return value.replace(/\${input\.([^}]+)}/g, (match: string, field: string) => {
          const fieldPath = field.split('.');
          let result: unknown = inputData;

          for (const path of fieldPath) {
            if (result && typeof result === 'object' && path in (result as Record<string, unknown>)) {
              result = (result as Record<string, unknown>)[path];
            } else {
              return match; // Mantém o placeholder se o caminho não existir
            }
          }

          return typeof result === 'string' ? result : JSON.stringify(result);
        });
      } else if (Array.isArray(value)) {
        return value.map(processValue);
      } else if (value && typeof value === 'object') {
        const result: Record<string, unknown> = {};
        Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
          result[k] = processValue(v);
        });
        return result;
      }
      
      return value;
    };
    
    // Processa todos os campos da configuração
    Object.entries(formattedConfig).forEach(([key, value]) => {
      formattedConfig[key] = processValue(value);
    });
    
    return formattedConfig;
  }

  /**
   * Recupera os prompts associados a um workflow através do agente vinculado
   */
  static async getAgentPrompts(workflowId: string): Promise<Record<string, unknown>[]> {
    try {
      // Primeiro, buscamos o workflow para obter o agent_base_id
      try {
        const workflow = await WorkflowBaseService.getById(workflowId);
        
        if (!workflow?.agent_base_id) {
          console.warn('[NodeService] agent_base_id não encontrado no workflow');
          return [];
        }
        

        // Depois, buscamos os settings do agente que contém os prompts
        const { data: settings, error: agentError } = await AgentBaseService.getSettingsById(workflow.agent_base_id);

        if (agentError || !settings) {
          console.warn('[NodeService] Erro ao buscar settings do agente:', agentError || 'settings não encontrados');
          return [];
        }
        
        if (settings && typeof settings === 'object' && 'prompts' in settings && Array.isArray(settings.prompts)) {
          return settings.prompts;
        }
        
        console.warn('[NodeService] Campo prompts não encontrado ou não é um array:', settings);
        return [];
      } catch (error) {
        console.error('[NodeService] Erro ao buscar workflow:', error);
        return [];
      }
    } catch (error) {
      console.error('[NodeService] Erro ao recuperar prompts do agente:', error);
      return [];
    }
  }

  /**
   * Recupera os parâmetros (values) associados a um workflow através do agente vinculado
   */
  static async getAgentParameters(workflowId: string): Promise<Record<string, unknown>> {
    try {
      // Primeiro, buscamos o workflow para obter o agent_base_id
      try {
        const workflow = await WorkflowBaseService.getById(workflowId);
        
        if (!workflow?.agent_base_id) {
          console.warn('[NodeService] agent_base_id não encontrado no workflow');
          return {};
        }
        

        // Depois, buscamos os settings do agente que contém os parâmetros
        const { data: settings, error: agentError } = await AgentBaseService.getSettingsById(workflow.agent_base_id);

        if (agentError || !settings) {
          console.warn('[NodeService] Erro ao buscar settings do agente:', agentError || 'settings não encontrados');
          return {};
        }
        
        if (settings && typeof settings === 'object' && 'parameters' in settings && settings.parameters) {
          const parameters = settings.parameters as { values?: Record<string, unknown> } | null | undefined;

          // Extrair apenas os valores dos parâmetros
          const parameterValues = parameters?.values || {};
          
          return parameterValues;
        }
        
        console.warn('[NodeService] Campo parameters não encontrado:', settings);
        return {};
      } catch (error) {
        console.error('[NodeService] Erro ao buscar workflow:', error);
        return {};
      }
    } catch (error) {
      console.error('[NodeService] Erro ao recuperar parâmetros do agente:', error);
      return {};
    }
  }

  /**
   * Executa um node via API REST
   */
  static async executeNode(
    node: Node, 
    input: Record<string, unknown> = {},
    organizationId: string,
    workspaceId: string
  ): Promise<Record<string, unknown>> {
    // Log do node antes de qualquer processamento
    
    // Obter a configuração efetiva do node (apenas de node.data.config, pois node.config não existe no tipo Node)
    const nodeConfig = node.data?.config || {};
    
    // Preparar a configuração formatada
    const formattedConfig = this.formatNodeConfig(nodeConfig, input);
    
    // Tratamento especial para nodes do tipo mapping (e potencialmente outros tipos)
    if (node.data?.type === 'mapping' && formattedConfig.mapping && (formattedConfig.mapping as Record<string, unknown>).value) {
      // Extrair o array diretamente para o formato esperado pelo servidor
      formattedConfig.mapping = (formattedConfig.mapping as Record<string, unknown>).value;
    }
    
    // Recuperar os prompts do agente vinculado ao workflow
    let prompts: Record<string, unknown>[] = [];
    if (node.data?.workflowId) {
      prompts = await this.getAgentPrompts(node.data.workflowId);
    }
    
    // Recuperar os parâmetros do agente vinculado ao workflow
    let parameters: Record<string, unknown> = {};
    if (node.data?.workflowId) {
      parameters = await this.getAgentParameters(node.data.workflowId);
    }
    
    // Payload padronizado para qualquer tipo de node
    const payload = {
      id: node.id,
      name: node.data?.name || node.data?.type,
      type: node.data?.type,
      label: node.data?.label || node.data?.title,
      config: formattedConfig,
      input: input || {},
      prompt: node.data?.prompt || undefined,
      ...(prompts.length > 0 ? { prompts } : {}),
      ...(Object.keys(parameters).length > 0 ? { parameters } : {}),
      organizationId,
      productId: workspaceId,
      workflowId: node.data?.workflowId
    };
    
    
    // engineApiBase() já termina em /api
    const endpoint = `${engineApiBase()}/nodes/execute`;
    
    // Fazer a requisição para o backend usando o utility post
    try {
      const data = await postApi<Record<string, unknown>>(endpoint, payload);
      return data;
    } catch (error) {
      console.error('[NodeService] Erro na resposta do servidor:', error);
      throw error;
    }
  }

  /**
   * Versão compatível com a assinatura antiga do WorkflowService
   */
  static async executeNodeLegacy(nodeId: string, config: Record<string, unknown>, workflowId: string): Promise<Record<string, unknown>> {
    try {
      // Obter informações adicionais necessárias
      const { data: sessionData } = await SessionService.getSession();
      const userId = sessionData?.session?.user?.id;
      
      if (!userId) {
        throw new Error('Usuário não autenticado');
      }
      
      // Obter organizationId do workflow/produto (não do perfil do usuário)
      const workflow = await WorkflowBaseService.getById(workflowId);
      const workspaceId = workflow?.product_id;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const orgIdFromWorkflow = (workflow?.settings as any)?.organizationId || (workflow as any)?.organization_id;

      if (!workspaceId) {
        throw new Error('ProductId não encontrado no workflow');
      }

      if (!orgIdFromWorkflow) {
        throw new Error('OrganizationId não encontrado no workflow');
      }

      // Obter definição do nó
      const { data: nodeDefinition } = await NodeDefinitionService.getById(nodeId);

      if (!nodeDefinition) {
        throw new Error(`Definição do nó não encontrada: ${nodeId}`);
      }

      // Criar um node compatível com ReactFlow para usar com executeNode
      const node = {
        id: nodeId,
        data: {
          config,
          type: nodeDefinition.type,
          workflowId
        }
      };

      // Executar o nó usando a nova API
      return await this.executeNode(
        node as Node,
        {text: ""} as Record<string, unknown>,
        String(orgIdFromWorkflow),
        String(workspaceId)
      );
    } catch (error) {
      console.error('Erro ao executar node (legacy):', error);
      throw error;
    }
  }

  /**
   * Verifica o status de uma execução de node (nós async: openai_assistant/embedding).
   * Rota real é GET /nodes/status/:nodeId (engine, ambos backends) — o path antigo
   * `/nodes/executions/:id` nunca existiu em nenhum backend (404 silencioso).
   */
  static async checkNodeStatus(executionId: string): Promise<Record<string, unknown>> {
    try {
      return await getApi<Record<string, unknown>>(`${engineApiBase()}/nodes/status/${executionId}`);
    } catch (error) {
      throw new Error(`Erro ao verificar status do node: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  }

  /**
   * Faz polling do status de um node até terminar
   */
  static async pollNodeStatus(nodeExecutionId: string, maxAttempts = 60, interval = 1000): Promise<Record<string, unknown>> {
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      const status = await this.checkNodeStatus(nodeExecutionId);
      
      if (status.status === 'completed' || status.status === 'failed') {
        // endpoint de status devolve o payload em `result` (legacy e v2)
        return (status.output as Record<string, unknown>)
          || (status.result as Record<string, unknown>)
          || status;
      }
      
      // Esperar o intervalo antes da próxima tentativa
      await new Promise(resolve => setTimeout(resolve, interval));
      attempts++;
    }
    
    throw new Error('Tempo limite excedido ao aguardar conclusão do nó');
  }
}
